import type {
  ComparisonAnalysis,
  DashboardSummary,
  DistributionResponse,
  ExperimentCatalog,
  ExperimentCohortSummary,
  ExperimentId,
  ExperimentMapIndex,
  ExperimentMunicipalityItem,
  ExperimentMunicipalityList,
  AtlasContextProfile,
  AuditReviewCatalog,
  AuditReviewCase,
  AuditReviewDecision,
  AuditReviewStatus,
  AuditReviewSummary,
  AuditReviewUpdate,
  CasaCivilCategory,
  CasaCivilMapIndex,
  CasaCivilMunicipalityDetail,
  CasaCivilMunicipalityList,
  CasaCivilSummary,
  CutoffLabRequest,
  CutoffLabResponse,
  ExecutionCatalog,
  FilterMetadata,
  GeoFeatureCollection,
  Indicator,
  MapIndex,
  MunicipalityCodeList,
  MunicipalityDetail,
  MunicipalityExplanation,
  MunicipalityList,
  MunicipalityPeers,
  MunicipalitySummary,
  NumericIcmMapIndex,
  NumericIcmMunicipalityDetail,
  NumericIcmMunicipalityList,
  NumericIcmSummary,
  CohortScope,
  PopulationGroup,
  PopulationSegment,
  PopulationSegmentationOverview,
  PopulationSegmentMunicipalityList,
  PopulationSegmentProfile,
  PopulationSegmentMapIndex,
  RunInfo,
  ScenarioOptions,
  ScenarioRequest,
  ScenarioResponse,
  SavedScenarioCatalog,
  SavedScenarioCreate,
  SavedScenarioDetail,
  SavedScenarioVersionCreate,
  ScenarioLifecycleStatus,
  SensitivityResponse,
  SourceRevisionMunicipalityDetail,
  SourceRevisionMunicipalityList,
  SourceRevisionCasaCivilMunicipalityList,
  SourceRevisionCasaCivilSelectionView,
  SourceRevisionCasaCivilSummary,
  SourceRevisionPlotIndex,
  SourceRevisionReference,
  SourceRevisionSummary,
  SourceRevisionTransition,
  CasaCivilRankAtlasMunicipalityList,
  CasaCivilRankAtlasMapIndex,
  CasaCivilRankAtlasSummary,
  CasaCivilRankProfile,
  IcmCalibrationMunicipalityList,
  IcmCalibrationNormalization,
  IcmCalibrationScenarioList,
  IcmCalibrationSelectionView,
  IcmCalibrationStability,
  IcmCalibrationSummary,
  PnaAnaClasses,
  PnaAnaCriterionKey,
  PnaAnaMapIndex,
  PnaAnaMunicipalityDetail,
  PnaAnaMunicipalityList,
  PnaAnaRobustness,
  PnaAnaSummary,
} from './types'
import i18n, { appLanguage, type AppLanguage } from '../i18n'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export interface TerritorialFilters {
  uf?: string
  macroregion?: string
  selected?: boolean
  minimum_score?: number
  icm_class?: string
  minimum_population?: number
  maximum_population?: number
  in_casa_civil?: boolean
}

export interface MunicipalityFilters extends TerritorialFilters {
  indicator: Indicator
  query?: string
}

function queryString(params: object): string {
  const query = new URLSearchParams()
  Object.entries(params as Record<string, string | number | boolean | undefined>).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value))
  })
  return query.toString()
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    signal,
    headers: { Accept: 'application/json', 'Accept-Language': appLanguage() },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null
    const localizedDetail = typeof body?.detail === 'string' ? body.detail : undefined
    throw new Error(localizedDetail ?? i18n.t('common.apiFailure', { status: response.status }))
  }
  return (await response.json()) as T
}

async function postRequest<T>(path: string, body: object, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    signal,
    headers: {
      Accept: 'application/json',
      'Accept-Language': appLanguage(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { detail?: string } | null
    throw new Error(typeof payload?.detail === 'string' ? payload.detail : i18n.t('common.apiFailure', { status: response.status }))
  }
  return (await response.json()) as T
}

async function deleteRequest(path: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    signal,
    headers: { Accept: 'application/json', 'Accept-Language': appLanguage() },
  })
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { detail?: string } | null
    throw new Error(typeof payload?.detail === 'string' ? payload.detail : i18n.t('common.apiFailure', { status: response.status }))
  }
}

export const api = {
  summary: (signal?: AbortSignal) => request<DashboardSummary>('/summary', signal),
  currentRun: (signal?: AbortSignal) => request<RunInfo>('/runs/current', signal),
  executions: (signal?: AbortSignal) => request<ExecutionCatalog>('/runs', signal),
  filters: (signal?: AbortSignal) => request<FilterMetadata>('/filters', signal),
  methodology: (signal?: AbortSignal) => request<Record<string, unknown>>('/methodology', signal),
  ranking: (indicator: Indicator, limit = 10, signal?: AbortSignal) =>
    request<MunicipalitySummary[]>(`/rankings/${indicator}?limit=${limit}`, signal),
  municipality: (code: string, signal?: AbortSignal) =>
    request<MunicipalityDetail>(`/municipalities/${encodeURIComponent(code)}`, signal),
  municipalityExplanation: (code: string, signal?: AbortSignal) =>
    request<MunicipalityExplanation>(`/municipalities/${encodeURIComponent(code)}/explanation`, signal),
  municipalityPeers: (
    code: string,
    scope: CohortScope = 'national',
    populationGroup: PopulationGroup = 'all',
    limit = 5,
    signal?: AbortSignal,
  ) =>
    request<MunicipalityPeers>(
      `/municipalities/${encodeURIComponent(code)}/peers?${queryString({
        scope,
        population_group: populationGroup,
        limit,
      })}`,
      signal,
    ),
  municipalities: (
    params: MunicipalityFilters & {
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => {
    return request<MunicipalityList>(`/municipalities?${queryString(params)}`, signal)
  },
  municipalityCodes: (params: MunicipalityFilters, signal?: AbortSignal) =>
    request<MunicipalityCodeList>(`/map/municipality-codes?${queryString(params)}`, signal),
  compare: (codes: string[], signal?: AbortSignal) =>
    request<MunicipalityDetail[]>(`/compare?codes=${codes.join(',')}`, signal),
  comparisonAnalysis: (
    codes: string[],
    cohortScope: CohortScope = 'national',
    populationGroup: PopulationGroup = 'all',
    signal?: AbortSignal,
  ) =>
    request<ComparisonAnalysis>(
      `/compare/analysis?${queryString({
        codes: codes.join(','),
        cohort_scope: cohortScope,
        population_group: populationGroup,
      })}`,
      signal,
    ),
  sensitivity: (indicator: Indicator, signal?: AbortSignal) =>
    request<SensitivityResponse>(`/sensitivity/${indicator}`, signal),
  distribution: (indicator: Indicator, filters: TerritorialFilters = {}, signal?: AbortSignal) =>
    request<DistributionResponse>(`/distribution/${indicator}?${queryString(filters)}`, signal),
  populationSegmentation: (indicator: Indicator, signal?: AbortSignal) =>
    request<PopulationSegmentationOverview>(
      `/segmentation/population?${queryString({ indicator })}`,
      signal,
    ),
  populationSegmentMapIndex: (indicator: Indicator, signal?: AbortSignal) =>
    request<PopulationSegmentMapIndex>(
      `/segmentation/population/map-index?${queryString({ indicator })}`,
      signal,
    ),
  populationSegmentMunicipalities: (
    segment: PopulationSegment,
    params: {
      indicator: Indicator
      query?: string
      macroregion?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<PopulationSegmentMunicipalityList>(
    `/segmentation/population/${segment}?${queryString(params)}`,
    signal,
  ),
  populationSegmentProfile: (code: string, indicator: Indicator, signal?: AbortSignal) =>
    request<PopulationSegmentProfile>(
      `/segmentation/population/municipality/${encodeURIComponent(code)}?${queryString({ indicator })}`,
      signal,
    ),
  populationSegmentCsvUrl: (
    segment: PopulationSegment,
    params: { indicator: Indicator; query?: string; macroregion?: string },
  ) => `${API_BASE}/segmentation/population/${segment}/export.csv?${queryString({
    ...params,
    lang: appLanguage(),
  })}`,
  experiments: (signal?: AbortSignal) => request<ExperimentCatalog>('/experiments', signal),
  experimentCohorts: (experiment: ExperimentId, indicator: Indicator, signal?: AbortSignal) =>
    request<ExperimentCohortSummary[]>(
      `/experiments/${experiment}/cohorts?${queryString({ indicator })}`,
      signal,
    ),
  experimentMunicipalities: (
    experiment: ExperimentId,
    params: { indicator: Indicator; cohort_id?: string; query?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) => request<ExperimentMunicipalityList>(
    `/experiments/${experiment}/municipalities?${queryString(params)}`,
    signal,
  ),
  experimentMunicipality: (
    experiment: ExperimentId,
    code: string,
    indicator: Indicator,
    signal?: AbortSignal,
  ) => request<ExperimentMunicipalityItem>(
    `/experiments/${experiment}/municipality/${encodeURIComponent(code)}?${queryString({ indicator })}`,
    signal,
  ),
  experimentMapIndex: (experiment: ExperimentId, indicator: Indicator, signal?: AbortSignal) =>
    request<ExperimentMapIndex>(
      `/experiments/${experiment}/map-index?${queryString({ indicator })}`,
      signal,
    ),
  atlasContext: (code: string, signal?: AbortSignal) =>
    request<AtlasContextProfile>(`/context/atlas/municipality/${encodeURIComponent(code)}`, signal),
  experimentCsvUrl: (experiment: ExperimentId, indicator: Indicator, cohortId?: string) =>
    `${API_BASE}/experiments/${experiment}/export.csv?${queryString({
      indicator,
      cohort_id: cohortId,
      lang: appLanguage(),
    })}`,
  experimentReportUrl: `${API_BASE}/experiments/report.html`,
  sourceRevisionSummary: (signal?: AbortSignal) =>
    request<SourceRevisionSummary>('/source-revision/summary', signal),
  sourceRevisionPlotIndex: (signal?: AbortSignal) =>
    request<SourceRevisionPlotIndex>('/source-revision/plot-index', signal),
  sourceRevisionMunicipalities: (
    params: {
      reference: SourceRevisionReference
      query?: string
      transition?: SourceRevisionTransition
      icm_class?: string
      macroregion?: string
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<SourceRevisionMunicipalityList>(
    `/source-revision/municipalities?${queryString(params)}`,
    signal,
  ),
  sourceRevisionMunicipality: (code: string, signal?: AbortSignal) =>
    request<SourceRevisionMunicipalityDetail>(
      `/source-revision/municipalities/${encodeURIComponent(code)}`,
      signal,
    ),
  sourceRevisionCsvUrl: `${API_BASE}/source-revision/export.csv`,
  sourceRevisionWorkbookUrl: `${API_BASE}/source-revision/workbook.xlsx`,
  numericIcmSummary: (signal?: AbortSignal) =>
    request<NumericIcmSummary>('/numeric-icm/summary', signal),
  numericIcmMapIndex: (signal?: AbortSignal) =>
    request<NumericIcmMapIndex>('/numeric-icm/map-index', signal),
  numericIcmMunicipalities: (
    params: {
      query?: string
      uf?: string
      macroregion?: string
      icm_class?: string
      minimum_score?: number
      maximum_score?: number
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<NumericIcmMunicipalityList>(
    `/numeric-icm/municipalities?${queryString(params)}`,
    signal,
  ),
  numericIcmMunicipality: (code: string, signal?: AbortSignal) =>
    request<NumericIcmMunicipalityDetail>(
      `/numeric-icm/municipalities/${encodeURIComponent(code)}`,
      signal,
    ),
  numericIcmCsvUrl: `${API_BASE}/numeric-icm/export.csv`,
  sourceRevisionCasaCivilSummary: (signal?: AbortSignal) =>
    request<SourceRevisionCasaCivilSummary>('/source-revision/casa-civil/summary', signal),
  sourceRevisionCasaCivilMunicipalities: (
    params: {
      reference: SourceRevisionReference
      query?: string
      in_casa_civil?: boolean
      selected_view?: SourceRevisionCasaCivilSelectionView
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<SourceRevisionCasaCivilMunicipalityList>(
    `/source-revision/casa-civil/municipalities?${queryString(params)}`,
    signal,
  ),
  casaCivilRankAtlasSummary: (signal?: AbortSignal) =>
    request<CasaCivilRankAtlasSummary>('/source-revision/casa-civil/rank-atlas/summary', signal),
  casaCivilRankAtlasMapIndex: (signal?: AbortSignal) =>
    request<CasaCivilRankAtlasMapIndex>('/source-revision/casa-civil/rank-atlas/map-index', signal),
  casaCivilRankAtlasMunicipalities: (
    params: {
      query?: string
      macroregion?: string
      uf_abbr?: string
      profile?: CasaCivilRankProfile
      latest_selected?: boolean
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<CasaCivilRankAtlasMunicipalityList>(
    `/source-revision/casa-civil/rank-atlas/municipalities?${queryString(params)}`,
    signal,
  ),
  casaCivilRankAtlasCsvUrl: (params: {
    query?: string
    macroregion?: string
    uf_abbr?: string
    profile?: CasaCivilRankProfile
    latest_selected?: boolean
    sort?: string
  } = {}) => `${API_BASE}/source-revision/casa-civil/rank-atlas/export.csv?${queryString(params)}`,
  pnaAnaSummary: (signal?: AbortSignal) =>
    request<PnaAnaSummary>('/mc-pna-ana/summary', signal),
  pnaAnaMapIndex: (signal?: AbortSignal) =>
    request<PnaAnaMapIndex>('/mc-pna-ana/map-index', signal),
  pnaAnaMunicipalities: (
    params: {
      criterion?: PnaAnaCriterionKey
      query?: string
      macroregion?: string
      uf_abbr?: string
      pna_class?: string
      transition?: string
      casa_civil_only?: boolean
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<PnaAnaMunicipalityList>(
    `/mc-pna-ana/municipalities?${queryString(params)}`,
    signal,
  ),
  pnaAnaMunicipality: (code: string, signal?: AbortSignal) =>
    request<PnaAnaMunicipalityDetail>(
      `/mc-pna-ana/municipalities/${encodeURIComponent(code)}`,
      signal,
    ),
  pnaAnaClasses: (signal?: AbortSignal) =>
    request<PnaAnaClasses>('/mc-pna-ana/classes', signal),
  pnaAnaRobustness: (signal?: AbortSignal) =>
    request<PnaAnaRobustness>('/mc-pna-ana/robustness', signal),
  pnaAnaCsvUrl: `${API_BASE}/mc-pna-ana/export.csv`,
  icmCalibrationSummary: (signal?: AbortSignal) =>
    request<IcmCalibrationSummary>('/source-revision/icm-calibration/summary', signal),
  icmCalibrationScenarios: (
    params: {
      normalization?: IcmCalibrationNormalization
      institutional_weight?: number
      matched_volume?: boolean
    } = {},
    signal?: AbortSignal,
  ) => request<IcmCalibrationScenarioList>(
    `/source-revision/icm-calibration/scenarios?${queryString(params)}`,
    signal,
  ),
  icmCalibrationMunicipalities: (
    params: {
      query?: string
      transition?: SourceRevisionTransition
      stability?: IcmCalibrationStability
      in_casa_civil?: boolean
      selected_view?: IcmCalibrationSelectionView
      selected?: boolean
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<IcmCalibrationMunicipalityList>(
    `/source-revision/icm-calibration/municipalities?${queryString(params)}`,
    signal,
  ),
  icmCalibrationWorkbookUrl: `${API_BASE}/source-revision/icm-calibration/workbook.xlsx`,
  casaCivilSummary: (signal?: AbortSignal) => request<CasaCivilSummary>('/casa-civil/summary', signal),
  casaCivilMapIndex: (signal?: AbortSignal) => request<CasaCivilMapIndex>('/casa-civil/map-index', signal),
  casaCivilMunicipalities: (
    params: {
      query?: string
      category?: CasaCivilCategory
      macroregion?: string
      uf_abbr?: string
      population_band?: PopulationSegment
      gdp_band?: 'low' | 'middle' | 'high'
      casa_civil?: boolean
      registered?: boolean
      experiment?: ExperimentId
      selected?: boolean
      sort?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<CasaCivilMunicipalityList>(`/casa-civil/municipalities?${queryString(params)}`, signal),
  casaCivilMunicipality: (code: string, signal?: AbortSignal) =>
    request<CasaCivilMunicipalityDetail>(`/casa-civil/municipalities/${encodeURIComponent(code)}`, signal),
  casaCivilCsvUrl: (params: Record<string, string | number | boolean | undefined>) =>
    `${API_BASE}/casa-civil/export.csv?${queryString({ ...params, lang: appLanguage() })}`,
  casaCivilGeoJsonUrl: (params: Record<string, string | number | boolean | undefined>) =>
    `${API_BASE}/casa-civil/export.geojson?${queryString({ ...params, lang: appLanguage() })}`,
  casaCivilPdfUrl: (code: string, lang: AppLanguage = appLanguage()) =>
    `${API_BASE}/casa-civil/municipalities/${encodeURIComponent(code)}/report.pdf?${queryString({ lang })}`,
  casaCivilAuditUrl: `${API_BASE}/casa-civil/audit.html`,
  auditReviewSummary: (signal?: AbortSignal) =>
    request<AuditReviewSummary>('/casa-civil/reviews/summary', signal),
  auditReviews: (
    params: {
      query?: string
      review_status?: AuditReviewStatus
      decision?: AuditReviewDecision
      driver?: string
      priority?: string
      macroregion?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal,
  ) => request<AuditReviewCatalog>(`/casa-civil/reviews?${queryString(params)}`, signal),
  auditReview: (code: string, signal?: AbortSignal) =>
    request<AuditReviewCase>(`/casa-civil/reviews/${encodeURIComponent(code)}`, signal),
  updateAuditReview: (code: string, payload: AuditReviewUpdate, signal?: AbortSignal) =>
    postRequest<AuditReviewCase>(`/casa-civil/reviews/${encodeURIComponent(code)}`, payload, signal),
  calculateCutoffLab: (payload: CutoffLabRequest, signal?: AbortSignal) =>
    postRequest<CutoffLabResponse>('/cutoff-lab/calculate', payload, signal),
  scenarioOptions: (signal?: AbortSignal) => request<ScenarioOptions>('/scenarios/options', signal),
  calculateScenario: (payload: ScenarioRequest, signal?: AbortSignal) =>
    postRequest<ScenarioResponse>('/scenarios/calculate', payload, signal),
  savedScenarios: (signal?: AbortSignal) => request<SavedScenarioCatalog>('/scenarios', signal),
  savedScenario: (scenarioId: string, signal?: AbortSignal) =>
    request<SavedScenarioDetail>(`/scenarios/${encodeURIComponent(scenarioId)}`, signal),
  deleteSavedScenario: (scenarioId: string, signal?: AbortSignal) =>
    deleteRequest(`/scenarios/${encodeURIComponent(scenarioId)}`, signal),
  createSavedScenario: (payload: SavedScenarioCreate, signal?: AbortSignal) =>
    postRequest<SavedScenarioDetail>('/scenarios', payload, signal),
  createSavedScenarioVersion: (
    scenarioId: string,
    payload: SavedScenarioVersionCreate,
    signal?: AbortSignal,
  ) => postRequest<SavedScenarioDetail>(
    `/scenarios/${encodeURIComponent(scenarioId)}/versions`,
    payload,
    signal,
  ),
  updateSavedScenarioStatus: (
    scenarioId: string,
    status: ScenarioLifecycleStatus,
    actor: string,
    changeNote: string,
    signal?: AbortSignal,
  ) => postRequest<SavedScenarioDetail>(
    `/scenarios/${encodeURIComponent(scenarioId)}/status`,
    { status, actor, change_note: changeNote },
    signal,
  ),
  scenarioManifestUrl: (scenarioId: string, version: number) =>
    `${API_BASE}/scenarios/${encodeURIComponent(scenarioId)}/versions/${version}/manifest.json`,
  scenarioResultsCsvUrl: (scenarioId: string, version: number) =>
    `${API_BASE}/scenarios/${encodeURIComponent(scenarioId)}/versions/${version}/results.csv`,
  methodologyReportUrl: `${API_BASE}/methodology/report.html`,
  geojson: (signal?: AbortSignal) => request<GeoFeatureCollection>('/geo/municipalities/national', signal),
  mapIndex: (signal?: AbortSignal) => request<MapIndex>('/map/index', signal),
  municipalityPdfUrl: (code: string, lang: AppLanguage = appLanguage()) =>
    `${API_BASE}/municipalities/${encodeURIComponent(code)}/report.pdf?${queryString({ lang })}`,
  comparisonPdfUrl: (
    codes: string[],
    cohortScope: CohortScope = 'national',
    populationGroup: PopulationGroup = 'all',
    lang: AppLanguage = appLanguage(),
  ) =>
    `${API_BASE}/compare/report.pdf?${queryString({
      codes: codes.join(','),
      cohort_scope: cohortScope,
      population_group: populationGroup,
      lang,
    })}`,
  consolidatedComparisonPdfUrl: (
    codes: string[],
    cohortScope: CohortScope = 'national',
    populationGroup: PopulationGroup = 'all',
    lang: AppLanguage = appLanguage(),
  ) =>
    `${API_BASE}/compare/consolidated-report.pdf?${queryString({
      codes: codes.join(','),
      cohort_scope: cohortScope,
      population_group: populationGroup,
      lang,
    })}`,
  comparisonCsvUrl: (
    codes: string[],
    cohortScope: CohortScope = 'national',
    populationGroup: PopulationGroup = 'all',
    lang: AppLanguage = appLanguage(),
  ) =>
    `${API_BASE}/compare/analysis.csv?${queryString({
      codes: codes.join(','),
      cohort_scope: cohortScope,
      population_group: populationGroup,
      lang,
    })}`,
  filteredCsvUrl: (params: MunicipalityFilters) =>
    `${API_BASE}/exports/municipalities.csv?${queryString({ ...params, lang: appLanguage() })}`,
  filteredGeoJsonUrl: (params: MunicipalityFilters) =>
    `${API_BASE}/exports/municipalities.geojson?${queryString({ ...params, lang: appLanguage() })}`,
  csvUrl: `${API_BASE}/data/municipalities.csv`,
  openApiUrl: `${API_BASE.replace(/\/v1$/, '')}/openapi.json`,
}
